/**
 * WPP Concierge — Bootstrap Loader
 * ─────────────────────────────────────────────────────────────
 * This is the ONLY file the practice pastes into their site.
 * It's intentionally tiny (~1KB minified) so it loads instantly
 * and never blocks the page. It then loads the real widget
 * asynchronously from our CDN.
 *
 * What the practice pastes (one line, anywhere in <body> or end of <head>):
 *
 *   <script async src="https://cdn.wildernesspointpartners.com/concierge.js"
 *           data-practice="example-ortho"></script>
 *
 * That's it. Everything else is config-driven from our backend.
 */

(function () {
  // ----- Configuration from script tag attributes -----
  const currentScript =
    document.currentScript ||
    (function () {
      const scripts = document.getElementsByTagName('script');
      return scripts[scripts.length - 1];
    })();

  const practiceSlug = currentScript?.getAttribute('data-practice');
  const apiBase =
    currentScript?.getAttribute('data-api') ||
    'https://api.wildernesspointpartners.com';
  // Forwarded to the iframe URL so the widget can auto-open on the
  // /widget-preview/[slug] sandbox without changing the per-practice
  // widget_settings.  Hosts can opt-in with data-autoopen="1".
  const autoOpenAttr = currentScript?.getAttribute('data-autoopen');

  if (!practiceSlug) {
    console.warn('[WPP Concierge] data-practice attribute missing — widget will not load.');
    return;
  }

  // ----- Idempotency guard -----
  if (window.__wppConciergeLoaded) return;
  window.__wppConciergeLoaded = true;

  // ----- Visitor identity (anonymous-friendly) -----
  const VISITOR_KEY = 'wpp_visitor_id';
  let visitorId = localStorage.getItem(VISITOR_KEY);
  if (!visitorId) {
    visitorId =
      'v_' +
      Date.now().toString(36) +
      '_' +
      Math.random().toString(36).slice(2, 10);
    try {
      localStorage.setItem(VISITOR_KEY, visitorId);
    } catch (e) {
      /* private browsing — use session-only id */
    }
  }
  const sessionId =
    's_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);

  // ----- Mount the iframe widget -----
  // Iframe isolation gives us:
  //   - No CSS leakage from/to host site
  //   - Independent JS execution
  //   - HIPAA-friendly origin separation
  const mount = function () {
    const iframe = document.createElement('iframe');
    iframe.id = 'wpp-concierge-frame';
    iframe.title = 'Practice Concierge';
    iframe.setAttribute(
      'allow',
      'microphone; clipboard-write'
    );
    iframe.setAttribute('aria-label', 'Open concierge assistant');

    const params = new URLSearchParams({
      practice: practiceSlug,
      visitor: visitorId,
      session: sessionId,
      page: location.href,
      ref: document.referrer || '',
    });
    if (autoOpenAttr === '1' || autoOpenAttr === 'true') {
      params.set('autoopen', '1');
    }

    iframe.src = `${apiBase}/widget/v1/?${params.toString()}`;

    Object.assign(iframe.style, {
      position: 'fixed',
      bottom: '0',
      right: '0',
      width: '420px',
      height: '640px',
      maxWidth: '100vw',
      maxHeight: '100vh',
      border: '0',
      zIndex: '2147483646',
      colorScheme: 'normal',
      background: 'transparent',
    });

    document.body.appendChild(iframe);

    // ----- Phone detection (host context) -----
    // A real mobile device: coarse pointer AND a small physical screen.
    // This is deliberately narrower than "narrow viewport" so a small
    // desktop window keeps the unchanged desktop panel.
    const isPhone = function () {
      try {
        var coarse = window.matchMedia('(pointer: coarse)').matches;
        var minDim = Math.min(screen.width || 9999, screen.height || 9999);
        return coarse && minDim <= 480;
      } catch (e) {
        return false;
      }
    };

    // Tell the widget about its host environment so it can pick the
    // half-sheet layout + do the drag math against the true viewport.
    const postEnv = function () {
      try {
        if (iframe.contentWindow) {
          iframe.contentWindow.postMessage(
            {
              source: 'wpp-concierge-host',
              type: 'env',
              isPhone: isPhone(),
              viewportH: window.innerHeight,
            },
            '*'
          );
        }
      } catch (e) {
        /* cross-origin timing — the widget also re-pings on demand */
      }
    };

    // Apply an iframe size for a resize message from the widget.  Desktop
    // is unchanged (fixed 420×640 panel / 80×80 launcher).  On phones the
    // expanded panel becomes a bottom sheet: full width, height driven by
    // the widget's mode (half ≈ 50vh, full = viewport, drag = live px).
    let lastExpand: any = null; // remember the last expanded message for re-apply on rotation
    const applyResize = function (data: any) {
      if (data.state === 'collapsed') {
        lastExpand = null;
        iframe.style.width = '80px';
        iframe.style.height = '80px';
        iframe.style.right = '0';
        return;
      }
      if (data.state !== 'expanded') return;

      if (!isPhone()) {
        iframe.style.width = '420px';
        iframe.style.height = '640px';
        iframe.style.right = '0';
        return;
      }

      lastExpand = data;
      const vh = window.innerHeight;
      let h;
      if (data.mode === 'drag' && typeof data.heightPx === 'number') {
        h = data.heightPx;
      } else if (data.mode === 'full') {
        h = vh;
      } else {
        h = Math.round(vh * 0.5); // half sheet (default)
      }
      h = Math.max(120, Math.min(vh, h));
      iframe.style.width = '100vw';
      iframe.style.height = h + 'px';
      iframe.style.right = '0';
    };

    // The iframe sends postMessage when collapsed vs expanded (and while
    // dragging) so we can resize accordingly.  It also pings 'ready' so
    // we hand back the host environment.
    window.addEventListener('message', function (e) {
      if (e.source !== iframe.contentWindow) return;
      const data = e.data;
      if (!data || data.source !== 'wpp-concierge') return;

      if (data.type === 'ready') {
        postEnv();
      } else if (data.type === 'resize') {
        applyResize(data);
      } else if (data.type === 'hide') {
        // Business Value Engine paused for this practice (migration 0105).
        // The widget renders nothing, so hide the frame too — an invisible
        // 420×640 iframe would otherwise swallow clicks on the practice's
        // own page.  The snippet stays exactly as pasted; resuming restores
        // the widget on the next page load with no site change or redeploy.
        iframe.style.display = 'none';
      }
    });

    // Keep the widget's env current and re-apply the sheet size when the
    // host viewport changes (rotation, mobile URL-bar show/hide, resize).
    const onViewportChange = function () {
      postEnv();
      if (lastExpand) applyResize(lastExpand);
    };
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('orientationchange', onViewportChange);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mount);
  } else {
    mount();
  }
})();
